import React from "react";
import Seo from "../components/Seo";

const FAQ_CEE = [
  ["Qu'est-ce que la fiche AGRI-EQ-110 ?", "La fiche AGRI-EQ-110 est une fiche d'opération standardisée des Certificats d'Économies d'Énergie qui définit les conditions d'éligibilité et le montant de la prime pour un système de séchage solaire dans le secteur agricole et forestier."],
  ["Quel est le montant de la prime AGRI-EQ-110 ?", "Le montant est exprimé en kWh cumac par kW thermique installé, selon la zone climatique (H1, H2, H3) et le type de produits séchés : de 42 700 à 134 100 kWh cumac/kW pour un système complet neuf, de 12 200 à 24 100 kWh cumac/kW pour une toiture solaire couplée à un système existant."],
  ["Quelles sont les conditions de la fiche AGRI-EQ-110 ?", "Mise en place par un professionnel, bâtiment de séchage fermé, panneaux solaires hybrides certifiés IEC 61215 et IEC 61730, puissance thermique mesurée selon ISO 9806 ou norme ETV, productivité des capteurs ≥ 500 W/m², et système complet neuf à basse température 25-40 °C ou toiture solaire couplée à un système existant à haute température 60-80 °C."],
  ["Un panneau PVT glycol est-il éligible AGRI-EQ-110 ?", "Non : la fiche exige un flux d'air en sous-face du panneau. Un panneau hybride à circulation de glycol (fluide caloporteur liquide) ne répond pas à cette définition."],
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_CEE.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  },
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Fiche CEE AGRI-EQ-110 : séchage solaire par insufflation des produits agricoles et forestiers",
    description:
      "Conditions d'éligibilité, montants en kWh cumac par zone climatique et constitution du dossier CEE AGRI-EQ-110 pour un séchoir solaire à panneaux hybrides.",
    image: "https://thermasol-airsystems.fr/images/seo/hero.webp",
    author: { "@type": "Organization", name: "Thermasol Air Systems" },
    publisher: {
      "@type": "Organization",
      name: "Thermasol Air Systems",
      logo: { "@type": "ImageObject", url: "https://thermasol-airsystems.fr/logo192.png" },
    },
    mainEntityOfPage: "https://thermasol-airsystems.fr/fiche-cee-agri-eq-110/",
  },
];

const H2 = ({ children }) => (
  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-12 mb-5">{children}</h2>
);

const MONTANTS = [
  ["Système complet neuf", "H1", "42 700", "102 600"],
  ["Système complet neuf", "H2", "48 500", "116 600"],
  ["Système complet neuf", "H3", "55 700", "134 100"],
  ["Toiture solaire couplée à un système existant", "H1", "12 200", "16 900"],
  ["Toiture solaire couplée à un système existant", "H2", "13 900", "19 300"],
  ["Toiture solaire couplée à un système existant", "H3", "17 400", "24 100"],
];

export default function FicheCeeAgriEq110Page() {
  return (
    <main data-testid="fiche-cee-page" className="bg-white">
      <Seo
        title="Fiche CEE AGRI-EQ-110 | Séchage solaire par insufflation | Thermasol Air Systems®"
        description="Fiche CEE AGRI-EQ-110 : séchage solaire par insufflation des produits agricoles et forestiers avec panneaux solaires hybrides. Conditions, montant en kWh cumac, dossier monté par Thermasol."
        canonical="https://thermasol-airsystems.fr/fiche-cee-agri-eq-110/"
        ogImage="https://thermasol-airsystems.fr/images/seo/hero.webp"
        jsonLd={jsonLd}
      />

      <section className="pt-28 pb-10 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight">
            Fiche CEE AGRI-EQ-110 : la prime pour votre séchoir solaire
          </h1>
          <p className="text-slate-300 mt-5 leading-relaxed">
            La fiche d'opération standardisée AGRI-EQ-110 des Certificats d'Économies d'Énergie finance le séchage
            solaire par insufflation des produits et co-produits agricoles et forestiers utilisant des panneaux
            solaires hybrides. Thermasol Air Systems® conçoit des installations conformes et monte le dossier pour vous.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 md:px-6 pb-20">
        <H2>Secteur d'application</H2>
        <p className="text-slate-700">Agriculture — produits et co-produits agricoles et forestiers.</p>

        <H2>Dénomination de l'opération AGRI-EQ-110</H2>
        <p className="text-slate-700 leading-relaxed">
          Système complet de séchage par insufflation d'air utilisant des panneaux solaires hybrides (photovoltaïques
          et thermiques), ou toiture solaire en panneaux solaires hybrides couplée à un système d'insufflation d'air
          existant. Le panneau solaire hybride dispose d'un échangeur de chaleur sur sa face arrière qui transmet
          l'énergie thermique au flux d'air circulant en sous-face.
        </p>

        <H2>Conditions pour la délivrance des certificats</H2>
        <ul className="list-disc pl-6 space-y-2 text-slate-700">
          <li>Mise en place par un professionnel.</li>
          <li>Bâtiment de séchage fermé.</li>
          <li>Panneaux solaires hybrides certifiés IEC 61215 et IEC 61730.</li>
          <li>Puissance thermique mesurée selon ISO 9806 ou norme ETV.</li>
          <li>Productivité des capteurs ≥ 500 W/m² de surface d'entrée (électrique + thermique).</li>
          <li>Système complet neuf à basse température 25-40 °C OU toiture solaire couplée à un système existant à haute température 60-80 °C.</li>
          <li>Le système complet neuf comprend a minima : panneaux solaires hybrides, un ou plusieurs ventilateurs, une chambre d'aspiration, une chambre de compression (couloir de ventilation).</li>
        </ul>

        <H2>Montant des certificats en kWh cumac</H2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="table-montants-cee">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="border border-slate-300 px-4 py-3 text-left">Type d'installation</th>
                <th className="border border-slate-300 px-4 py-3">Zone climatique</th>
                <th className="border border-slate-300 px-4 py-3">Produits agricoles<br />(kWh cumac / kW th × P)</th>
                <th className="border border-slate-300 px-4 py-3">Produits forestiers<br />(kWh cumac / kW th × P)</th>
              </tr>
            </thead>
            <tbody>
              {MONTANTS.map(([type, zone, agri, forest], i) => (
                <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                  <td className="border border-slate-300 px-4 py-2 text-slate-700">{type}</td>
                  <td className="border border-slate-300 px-4 py-2 text-center font-semibold text-slate-800">{zone}</td>
                  <td className="border border-slate-300 px-4 py-2 text-center text-slate-700">{agri}</td>
                  <td className="border border-slate-300 px-4 py-2 text-center text-orange-600 font-semibold">{forest}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-slate-600 text-sm mt-3">Montants par kW thermique installé (× P). Durée de vie conventionnelle : 15 ans.</p>

        <H2>Preuve de réalisation</H2>
        <p className="text-slate-700 leading-relaxed">
          La preuve de réalisation mentionne les marques et références des panneaux solaires hybrides, des ventilateurs,
          de la chambre d'aspiration et de la chambre de compression. Le document justificatif est la certification des
          panneaux installés.
        </p>

        <H2>Pourquoi le séchoir Thermasol est conforme AGRI-EQ-110</H2>
        <p className="text-slate-700 leading-relaxed">
          Le <a href="/sechoir-solaire" className="text-orange-600 font-semibold underline" data-testid="link-sechoir-solaire">séchoir solaire</a>{" "}
          Thermasol repose sur un panneau PVT air avec échangeur de chaleur sur la face arrière et flux d'air en
          sous-face de chaque panneau, certifié IEC 61215 / IEC 61730. C'est un système complet neuf livré avec
          ventilateurs, chambre d'aspiration et chambre de compression (couloir de ventilation).
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a href="/sechoir-solaire" data-testid="cta-vers-sechoir" className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-8 py-4 rounded-full text-center transition-colors">
            Découvrir le séchoir solaire Thermasol
          </a>
          <a href="/sechoir-solaire#etude" data-testid="cta-etude-cee" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full text-center transition-colors">
            Demander mon étude gratuite
          </a>
        </div>
      </div>
    </main>
  );
}
